/**
 * Frontend mirror of the Python `roboto.domain.custom_fields` record types
 * (`packages/roboto/src/roboto/domain/custom_fields/record.py`). Enum string
 * values and field names match the wire shape exactly.
 */

export enum CustomFieldStatus {
  Creating = "creating",
  Ready = "ready",
  Deleting = "deleting",
  Failed = "failed",
}

export enum CustomFieldType {
  String = "string",
  Number = "number",
  Timestamp = "timestamp",
  Boolean = "boolean",
  Enum = "enum",
}

export enum TargetEntityType {
  Collection = "collection",
  Dataset = "dataset",
  Device = "device",
  Event = "event",
  Session = "session",
}

export interface EnumFieldOptions {
  field_type: CustomFieldType.Enum;
  enum_values: string[];
}

export type CustomFieldOptions = EnumFieldOptions;

export interface CustomFieldRecord {
  field_id: string;
  org_id: string;
  field_name: string;
  display_name: string | null;
  description: string | null;
  field_type: CustomFieldType;
  status: CustomFieldStatus;
  entity_type: TargetEntityType;
  options: CustomFieldOptions | null;
  created: string;
  created_by: string;
  modified: string;
  modified_by: string;
  metadata_path: string | null;
  last_error: string | null;
  attempts: number;
}

export interface ListCustomFieldsRequest {
  entity_type?: TargetEntityType;
  // At least one status is required by the backend.
  statuses: CustomFieldStatus[];
  page_token?: string;
}

export interface CreateCustomFieldRequest {
  entity_type: TargetEntityType;
  field_name: string;
  field_type: CustomFieldType;
  display_name?: string;
  description?: string;
  // Required when field_type is Enum; declares the allowed values.
  options?: CustomFieldOptions;
}

export interface UpdateCustomFieldRequest {
  // Omit a field to leave it unchanged (NotSet); send `null` to clear it; send
  // a string to set it.
  display_name?: string | null;
  description?: string | null;
  // Replaces the field's options. For an enum field, must include every
  // existing value: values can be added but not removed.
  options?: CustomFieldOptions;
}

/** How a unique values search is matched against a field's stored values. */
export type CustomFieldValueMatchMode = "prefix" | "substring";

/**
 * Narrows a {@link CustomFieldUniqueValuesResponse} request. Every field is
 * optional; the server's defaults are a prefix, case-insensitive match and 20
 * values.
 */
export interface CustomFieldUniqueValuesRequest {
  /** Text matched literally against each value. Empty matches every value. */
  search?: string;
  match?: CustomFieldValueMatchMode;
  caseSensitive?: boolean;
  /** Most values to return, from 1 to 50. */
  limit?: number;
}

/** A distinct stored value of a custom field and how many entities hold it. */
export interface CustomFieldValueCount {
  value: string;
  count: number;
}

/** A String custom field's unique stored values, most common first. */
export interface CustomFieldUniqueValuesResponse {
  /** Matching values, ordered by count descending, then value ascending. */
  items: CustomFieldValueCount[];
  /** More values match than were returned. */
  truncated: boolean;
  /** Distinct values the field holds across the org, regardless of the search. */
  distinct_values: number;
  /** Entities in the org holding any value for the field, regardless of the search. */
  entities_with_value: number;
}
